import React, {
    useState,
    useRef,
    useMemo,
    useCallback,
    useEffect,
} from 'react';
import {
    Row,
    Table,
    Button,
    Col,
    Form,
    Space,
    Input,
    Divider,
    Modal,
    Typography,
} from 'antd';
import moment from 'moment';
import { isNil, is } from 'ramda';
import { pick } from '../../../utils/functions';
import { Props, RepeaterRow, Column } from './types';

import './style.less';

export const IGNORE_FIELD_PREFIX = 'ignore-';

// Process Initial Server Data
function processDataSource(dataSource) {
    return dataSource.map((row) => {
        let newRow = { ...row };

        for (const key in newRow) {
            // Assuming it's a date field by name
            // Create moment object
            if (key === `${IGNORE_FIELD_PREFIX}year`) {
                newRow[key] = moment(newRow[key]);
            }
        }

        return newRow;
    });
}

export function Repeater({
    name,
    form,
    columns,
    dataSource = [],
    buttonProps = { label: 'Add' },
    handleChange = () => {},
    header = '',
    rules = [],
    readonly = 0,
    className = 'one-column',
    label = null,
    _repeaterName = null,
    colSize = 12,
}: Props) {
    const repeaterName = _repeaterName ? _repeaterName : `repeater-${name}`;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [data, setData] = useState<RepeaterRow[]>(
        processDataSource(dataSource),
    );

    const rowIndex = useRef(data.length);
    const [editKey, setEditing] = useState(-1);

    const updatedColumns: Column[] = useMemo(() => {
        return columns.map(({ dataIndex, ...rest }) => ({
            ...rest,
            dataIndex: `${IGNORE_FIELD_PREFIX}${dataIndex}`,
        }));
    }, [columns]);

    const validationFields = updatedColumns.map((column) => column.dataIndex);

    async function isValidRow(row) {
        // Don't validate hidden column fields
        const reqFields = validationFields.filter((field) => {
            return !updatedColumns.some((col) => {
                return (
                    col.dataIndex === field &&
                    (col.hidden || col.skipValidation)
                );
            });
        });

        const newRow = pick(row, reqFields);

        try {
            await form.validateFields(reqFields);
        } catch (er) {
            return false;
        }

        if (Object.values(newRow).some((field) => typeof field === 'undefined'))
            return false;

        return true;
    }

    async function doEdit() {
        const row = form.getFieldsValue(validationFields);
        const newData = [...data];

        if (!(await isValidRow(row))) return;

        newData.splice(editKey, 1, {
            ...row,
            key: editKey,
        });

        setData(newData);

        // Reset
        cancelEdit();

        setIsModalVisible(false);
    }

    const cancelEdit = useCallback(() => {
        form.resetFields(validationFields);
        setEditing(-1);
    }, [validationFields, form]);

    const prepareEdit = useCallback(
        (key) => {
            setIsModalVisible(true);
            setEditing(key);

            const reqFields = validationFields.filter((field) => {
                return !updatedColumns.some((col) => {
                    return col.dataIndex === field && col.hidden;
                });
            });

            form.resetFields(reqFields);

            // Get current row
            const row = data.find((_row, index) => index === key);

            // Update fields to current editing
            form.setFieldsValue(row);

            // Focus first column field
            form.getFieldInstance(updatedColumns[0].dataIndex)?.focus();
        },
        [updatedColumns, data, form],
    );

    const doDelete = useCallback(
        (key: number) => {
            const newData = data.filter(
                (record: RepeaterRow, index) => index !== key,
            );

            cancelEdit();

            setData(newData);
        },
        [data, cancelEdit],
    );

    const actions: Column[] = useMemo(
        () => [
            {
                key: 'actions',
                dataIndex: 'actions',
                title: '',
                render: (_, record, index) => (
                    <Space size="small">
                        <Button
                            onClick={() => prepareEdit(index)}
                            disabled={!!readonly}
                        >
                            {readonly ? 'View' : 'Edit'}
                        </Button>
                        <Button
                            onClick={() => doDelete(index)}
                            disabled={!!readonly}
                        >
                            Delete
                        </Button>
                    </Space>
                ),
            },
        ],
        [doDelete, prepareEdit, readonly],
    );

    async function onAdd() {
        const row = form.getFieldsValue(validationFields);
        const newData = [...data];
        if (!(await isValidRow(row))) return;

        // Set row key
        newData.push({ key: rowIndex.current++, ...row });
        setData(newData);

        // Clear form fields
        form.resetFields(validationFields);
        setIsModalVisible(false);
    }

    // Set form repeater value when data changes
    useEffect(() => {
        handleChange(data);
        form.setFieldsValue({
            [repeaterName]: data,
        });
    }, [data, form, repeaterName, handleChange]);

    // Process data for display in Repeater Table
    const processedData = useMemo(() => {
        return data.map((row) => {
            let newRow = { ...row };

            for (const key in newRow) {
                if (!isNil(newRow[key] && is(Object, newRow[key]))) {
                    // Show Year for Moment objects
                    if (newRow[key]._isAMomentObject) {
                        newRow[key] = newRow[key].year();
                    }

                    // Show filename for Upload
                    if (Array.isArray(newRow[key])) {
                        if (newRow[key][0].name) {
                            newRow[key] = newRow[key][0].name;
                        } else {
                            newRow[key] = newRow[key].join('\n');
                        }
                    }
                }
            }

            return newRow;
        });
    }, [data]);

    const { label: btnLabel, ...restBtnProps } = buttonProps;
    const required = Boolean(rules && rules.length);
    return (
        <>
            <div className="hidden-input">
                <Form.Item
                    name={repeaterName}
                    label={label}
                    rules={rules}
                    required={required}
                    hidden={!label}
                >
                    <Input disabled />
                </Form.Item>
            </div>
            <Modal
                width={800}
                title={label ? label : 'Add entry'}
                visible={isModalVisible}
                onOk={() => onAdd()}
                onCancel={() => setIsModalVisible(false)}
                destroyOnClose={true}
                footer={[
                    editKey !== -1 ? (
                        <>
                            <Button
                                key="save"
                                type="primary"
                                onClick={async () => {
                                    await doEdit();
                                }}
                                disabled={!!readonly}
                            >
                                Save
                            </Button>
                            <Divider type="vertical" />
                            <Button
                                key="cancel"
                                onClick={async () => {
                                    cancelEdit();
                                    setIsModalVisible(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </>
                    ) : (
                        <Button
                            key="add"
                            type="primary"
                            onClick={async () => {
                                await onAdd();
                            }}
                            {...restBtnProps}
                        >
                            {btnLabel}
                        </Button>
                    ),
                ]}
            >
                <div className="ant-form-vertical input-no-margin">
                    <Row gutter={[16, 0]}>
                        {updatedColumns.map(
                            ({ Component = () => null, dataIndex, hidden }) => (
                                <Col xs={colSize} key={`col-${dataIndex}`}>
                                    <Component
                                        key={dataIndex}
                                        // Set Form.Item name to `dataIndex`
                                        // Hidden columns shouldn't have a `name` attribute
                                        name={hidden ? undefined : dataIndex}
                                        rules={[
                                            {
                                                required: true,
                                            },
                                        ]}
                                        required={true}
                                    />
                                </Col>
                            ),
                        )}
                    </Row>
                </div>
            </Modal>
            <>
                {header ? (
                    <Divider orientation="left">{header}</Divider>
                ) : (
                    <></>
                )}
                <Row justify="space-between" gutter={[8, 8]}>
                    <Col xs={24}>
                        <Table
                            className="repeater-table"
                            scroll={{ x: true }}
                            columns={updatedColumns
                                .filter(({ fieldOnly }) => !fieldOnly)
                                .filter(({ hidden }) => !hidden)
                                .concat(actions)}
                            dataSource={processedData}
                            pagination={false}
                            locale={{
                                emptyText: () => (
                                    <Typography.Paragraph type="secondary">
                                        Use the button below to{' '}
                                        <strong>add</strong> new entries.
                                    </Typography.Paragraph>
                                ),
                            }}
                        />
                    </Col>
                </Row>
            </>
            <Divider>
                <Button
                    type="primary"
                    onClick={() => setIsModalVisible(true)}
                    disabled={!!readonly}
                >
                    {btnLabel}
                </Button>
            </Divider>
        </>
    );
}
